// src/features/PasswordResetPage.js

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TextField, Button, Typography, Alert, Box } from "@mui/material";
import {
  FruitifyContainer,
  FruitifyStack,
  RegistrationCard,
} from "../../../shared-components/Containers";
import isPasswordValidFormat from "../Utils";
import FormControl from "@mui/material/FormControl";
import { useApiClient } from "../../../api";
import { enqueueSnackbar } from "notistack";

const PasswordResetPage = () => {
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { uid, token } = useParams();
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const apiClient = useApiClient();

  useEffect(() => {
    if (!uid || !token) {
      setError("Invalid link.");
    }
  }, [uid, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submittedData = new FormData(e.currentTarget);
    const newPassword = submittedData.get("new-password");
    const confirmPassword = submittedData.get("confirm-password");
    const isValid = validateInputs(newPassword, confirmPassword);

    if (!isValid) return;
    try {
      const data = await apiClient.users.resetPassword({
        uid,
        token,
        password: newPassword,
      });

      if (data.success) {
        enqueueSnackbar("Password reset successful. Please sign in.", {
          variant: "success",
          autoHideDuration: 2000,
        });
        setTimeout(() => navigate("/login"), 1200);
      } else {
        setError(data.message || "Password reset failed.");
      }
    } catch (error) {
      setError("An error occurred. Please try again.");
    }
  };

  const validateInputs = (newPassword, confirmPassword) => {
    if (newPassword !== confirmPassword) {
      setPasswordError(true);
      setPasswordErrorMessage("Passwords do not match.");
      return;
    }

    const isValid = isPasswordValidFormat(
      newPassword,
      setPasswordError,
      setPasswordErrorMessage,
    );

    return isValid;
  };

  return (
    <FruitifyContainer>
      <FruitifyStack
        direction="column"
        justifyContent="space-between"
        data-testid={"fruitify-password-reset-page"}
        sx={{ height: "100%" }}
      >
        <RegistrationCard variant="outlined">
          <Typography variant="h4" gutterBottom>
            Reset Your Password
          </Typography>
          <Box
            component="form"
            onSubmit={(e) => handleSubmit(e)}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            <FormControl>
              <TextField
                label={"New Password"}
                name="new-password"
                placeholder="••••••"
                type="password"
                id="new-confirm-password"
                autoComplete="new-password"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={passwordError ? "error" : "primary"}
              />
            </FormControl>{" "}
            <FormControl>
              <TextField
                label={"Confirm Password"}
                error={passwordError}
                helperText={passwordErrorMessage}
                name="confirm-password"
                placeholder="••••••"
                type="password"
                id="confirm-password"
                autoComplete="confirm-password"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={passwordError ? "error" : "primary"}
              />
            </FormControl>
            <Button variant="contained" color="primary" type="submit" fullWidth>
              Reset Password
            </Button>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </RegistrationCard>
      </FruitifyStack>
    </FruitifyContainer>
  );
};

export default PasswordResetPage;
